import { useState } from "react";
import { CalcCard, NumField, ResultRow, PrimaryButton, Section, SaveButton } from "@/components/CalcUI";
import { runCalc } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, XCircle, FolderOpen } from "lucide-react";
import TemplatePicker from "@/components/TemplatePicker";

const VERDICT = {
  GO: { color: "#10b981", icon: CheckCircle2, label: "GO" },
  CAUTION: { color: "#fbbf24", icon: AlertTriangle, label: "CAUTION" },
  STOP: { color: "#ef4444", icon: XCircle, label: "STOP" },
};

export default function KickTolerance() {
  const [inp, setInp] = useState({
    shoe_tvd: "4500",
    shoe_frac: "14.5",
    current_mw: "10.0",
    well_tvd: "10000",
    kick_intensity: "0.5",
    ann_cap: "0.0459",
    kick_fluid: "1.5",
  });
  const [out, setOut] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);

  const run = async () => {
    try {
      const parsed = Object.fromEntries(Object.entries(inp).map(([k, v]) => [k, parseFloat(v)]));
      const r = await runCalc("kick_tolerance", parsed);
      setOut(r);
      if (r.error) toast.error(r.error);
    } catch {
      toast.error("Invalid inputs");
    }
  };

  const V = VERDICT[out?.verdict];

  return (
    <div data-testid="kick-tolerance-page">
      <Section
        title="Kick Tolerance"
        subtitle="Maximum influx volume the well can safely handle before formation at the shoe breaks down. Use before trip, before continuing drilling, and any time mud weight or shoe strength changes."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CalcCard testid="kt-inputs" title="Well & Shoe Data" formula="H_max = (P_frac − P_form + (WD−SD)·0.052·MW)/(0.052·(MW−ρk))">
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="kt-shoe-tvd" label="Shoe TVD" unit="ft" value={inp.shoe_tvd} onChange={(v) => setInp({ ...inp, shoe_tvd: v })} />
              <NumField testid="kt-shoe-frac" label="Shoe frac gradient" unit="ppg" value={inp.shoe_frac} onChange={(v) => setInp({ ...inp, shoe_frac: v })} hint="From LOT" />
              <NumField testid="kt-mw" label="Current MW" unit="ppg" value={inp.current_mw} onChange={(v) => setInp({ ...inp, current_mw: v })} />
              <NumField testid="kt-well-tvd" label="Well TVD" unit="ft" value={inp.well_tvd} onChange={(v) => setInp({ ...inp, well_tvd: v })} />
              <NumField testid="kt-intensity" label="Kick intensity" unit="ppg" value={inp.kick_intensity} onChange={(v) => setInp({ ...inp, kick_intensity: v })} hint="ΔMW to kill" />
              <NumField testid="kt-anncap" label="Annular capacity" unit="bbl/ft" value={inp.ann_cap} onChange={(v) => setInp({ ...inp, ann_cap: v })} hint="Above shoe" />
              <NumField testid="kt-fluid" label="Kick fluid ρ" unit="ppg" value={inp.kick_fluid} onChange={(v) => setInp({ ...inp, kick_fluid: v })} hint="Gas ≈ 1.5, brine ≈ 8.7" />
            </div>
            <div className="mt-4 flex justify-between items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <PrimaryButton testid="kt-run" onClick={run}>Calculate Kick Tolerance</PrimaryButton>
                <button
                  data-testid="kt-tpl-btn"
                  onClick={() => setTplOpen(true)}
                  className="inline-flex items-center gap-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] text-[#94a3b8] px-3 py-2 mono text-xs uppercase tracking-widest transition-colors"
                >
                  <FolderOpen className="w-3.5 h-3.5" /> Templates
                </button>
              </div>
              <SaveButton getPayload={() => out && !out.error && { category: "Kick Tolerance", calculator: "kick_tolerance", inputs: inp, outputs: out, units: "field", default_name: "Kick Tolerance" }} />
            </div>
          </CalcCard>

          <div className="border border-[#334155] bg-[#11151c]">
            <div className="px-5 py-3 border-b border-[#334155]">
              <h3 className="heading text-sm font-bold uppercase tracking-wide">Result</h3>
            </div>
            <div className="p-5">
              {!out ? (
                <div className="text-sm mono text-[#94a3b8] py-8 text-center">
                  Fill inputs and hit calculate.
                </div>
              ) : out.error ? (
                <div className="border-l-2 border-[#ef4444] bg-[#ef4444]/10 px-3 py-2 text-xs mono text-[#fca5a5]">
                  {out.error}
                </div>
              ) : (
                <>
                  {V && (
                    <div
                      data-testid="kt-verdict"
                      className="flex items-center gap-3 border p-3 mb-4"
                      style={{ borderColor: V.color, backgroundColor: `${V.color}15` }}
                    >
                      <V.icon className="w-6 h-6" style={{ color: V.color }} />
                      <div>
                        <div className="mono text-xs tracking-widest" style={{ color: V.color }}>VERDICT</div>
                        <div className="heading text-xl font-bold" style={{ color: V.color }}>{V.label}</div>
                      </div>
                    </div>
                  )}
                  <ResultRow label="Kick tolerance" value={out.kick_tolerance_bbl} unit="bbl" emphasis />
                  <ResultRow label="Max kick height" value={out.max_kick_height_ft} unit="ft" />
                  <ResultRow label="Frac P @ shoe" value={out.frac_pressure_shoe_psi} unit="psi" />
                  <ResultRow label="Formation P" value={out.formation_pressure_psi} unit="psi" />
                  <ResultRow label="Current shoe P" value={out.shoe_pressure_current_psi} unit="psi" />
                  {out.warnings?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {out.warnings.map((w) => (
                        <div key={w} className="border-l-2 border-[#fbbf24] bg-[#f59e0b]/10 px-3 py-2 text-xs mono text-[#fbbf24]">
                          {w}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Section>

      <TemplatePicker
        open={tplOpen}
        onClose={() => setTplOpen(false)}
        calculator="kick_tolerance"
        onApply={(tpl) => {
          setInp({ ...inp, ...tpl.inputs });
          toast.success(`Loaded: ${tpl.name}`);
        }}
      />
    </div>
  );
}
